import React, { useMemo, useState } from "react"

import {
    DragDropContext,
    Draggable,
    type DropResult,
    Droppable,
} from "@hello-pangea/dnd"

import {
    AvailableCoverageHistorySortDirection,
    AvailableCoverageHistorySortField,
} from "@common-shared/config/coverageHistory.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { ICoverageHistoryListSort } from "@common-shared/types/internalApis/coverageHistory.types.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { CheckboxElement } from "@front-shared/elements/checkbox.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { PolicyHeaderDragDirection } from "../../../config/policies.config"
import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"

import sortDownIcon from "@front-shared/assets/images/icons/sort-down.svg"
import sortIcon from "@front-shared/assets/images/icons/sort-general.svg"

export const PoliciesListTableHeaderComponent: React.FunctionComponent<
    IPoliciesListTableHeaderComponentProps
> = props => {
    const policiesState = useAppSelector(policiesSelector)

    const activeColumns = useMemo<IPolicyColumnConfiguration[]>(() => {
        return props.columns.filter(item => item.isEnabled)
    }, [props.columns])

    const [isDragActive, setIsDragActive] = useState<boolean>(false)

    const allPoliciesSelected = useMemo<boolean>(() => {
        const policiesFiltered = props.isSinglePolicySelectable
            ? props.policies.filter(props.isSinglePolicySelectable)
            : props.policies

        if (!policiesFiltered.length) {
            return false
        }

        const selectedIds = new Set(
            props.selectedItems?.map(item => item.coverageResult.resultId),
        )

        return policiesFiltered.every(policy =>
            selectedIds.has(policy.coverageResult.resultId),
        )
    }, [props.policies, props.selectedItems, props.isSinglePolicySelectable])

    const onSortChanged = (newSortField: AvailableCoverageHistorySortField) => {
        props.onSetSortField?.(newSortField)
    }

    const onOrderChangeDragEnd = (result: DropResult) => {
        setIsDragActive(false)

        // if move to same position or moving handler is not passed
        if (
            !result.destination ||
            result.destination.index === result.source.index ||
            !props.onChangeColumnOrder
        ) {
            return
        }

        const columnLabelMoveSource = result.draggableId
        const columnLabelMoveTarget =
            activeColumns[result.destination.index].label

        // If moving forward, index of target will be more than source
        props.onChangeColumnOrder(
            columnLabelMoveSource,
            columnLabelMoveTarget,
            result.destination.index > result.source.index
                ? PolicyHeaderDragDirection.AFTER
                : PolicyHeaderDragDirection.BEFORE,
        )
    }

    return (
        <div className="table-row w-full">
            <div
                className={`
                    flex items-center bg-brand-white
                    ${!props.ignoreHorizontalPadding ? "px-[16px]" : ""}
                `}
            >
                {props.policiesSelectable && (
                    <div className="py-[8px] mr-[16px] p-[12px] -m-[12px]">
                        <CheckboxElement
                            name="selectAll"
                            defaultChecked={allPoliciesSelected}
                            watchDefaultCheckedChange={true}
                            onChange={newValue => {
                                // Match row checkboxes: ignore spurious onChange from CheckboxElement
                                // when synced from defaultChecked (it notifies on every isChecked change).
                                if (newValue === allPoliciesSelected) {
                                    return
                                }

                                if (newValue) {
                                    props.onSelectAllItems?.()
                                } else {
                                    props.onDeselectAllItems?.()
                                }
                            }}
                        />
                    </div>
                )}

                <DragDropContext
                    onDragEnd={onOrderChangeDragEnd}
                    onDragStart={() => {
                        setIsDragActive(true)
                    }}
                >
                    <Droppable
                        droppableId="droppable"
                        direction="horizontal"
                        isDropDisabled={
                            !props.onChangeColumnOrder ||
                            policiesState.policiesHeaderDragDropDisabled
                        }
                    >
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                className={`
                                    flex overflow-auto
                                    ${
                                        snapshot.isDraggingOver
                                            ? "bg-brand-lilacLight"
                                            : undefined
                                    }
                                `}
                                {...provided.droppableProps}
                            >
                                <span className="hidden">
                                    {provided.placeholder}
                                </span>

                                {activeColumns.map((columnConfig, index) => {
                                    // Sort is enabled for column if:
                                    // 1. Sort is enabled on the table for this list type
                                    // 2. Column has sortByField
                                    // 3. Column is not ignored for this list type
                                    const isSortEnabled =
                                        props.sortEnabled &&
                                        !!columnConfig.sortByField &&
                                        !columnConfig.listTypeSortIgnored?.[
                                            props.listType
                                        ]

                                    const isCurrentFieldMainSort =
                                        props.sortConfiguration?.sortBy ===
                                        columnConfig.sortByField

                                    return (
                                        <Draggable
                                            index={index}
                                            key={columnConfig.label}
                                            draggableId={columnConfig.label}
                                            isDragDisabled={
                                                !props.onChangeColumnOrder ||
                                                policiesState.policiesHeaderDragDropDisabled
                                            }
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...(provided
                                                            .draggableProps
                                                            .style || {}),

                                                        width: `${
                                                            columnConfig.width ||
                                                            POLICIES_COLUMNS_CONFIG.defaultColumnWidth
                                                        }px`,
                                                    }}
                                                    className={`
                                                        mr-[16px] relative flex-shrink-0 py-[8px]
                                                        ${
                                                            snapshot.isDragging
                                                                ? "bg-brand-lilac"
                                                                : "bg-brand-white"
                                                        }
                                                        ${
                                                            columnConfig.mouseflowIgnore
                                                                ? "mouseflow-ignore"
                                                                : ""
                                                        }
                                                        ${
                                                            isDragActive
                                                                ? "pl-[8px]"
                                                                : ""
                                                        }
                                                    `}
                                                >
                                                    <PrimaryText
                                                        className="opacity-75 flex items-center"
                                                        typography="h6"
                                                    >
                                                        {columnConfig.label}

                                                        {isSortEnabled ? (
                                                            <div
                                                                className={`
                                                                    cursor-pointer px-[8px] py-[2px]
                                                                    ${
                                                                        isCurrentFieldMainSort
                                                                            ? "transform scale-150"
                                                                            : ""
                                                                    }
                                                                `}
                                                                onClick={
                                                                    isSortEnabled
                                                                        ? () => {
                                                                              columnConfig.sortByField &&
                                                                                  onSortChanged(
                                                                                      columnConfig.sortByField,
                                                                                  )
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                <img
                                                                    className={
                                                                        isCurrentFieldMainSort &&
                                                                        props
                                                                            .sortConfiguration
                                                                            ?.sortDirection ===
                                                                            AvailableCoverageHistorySortDirection.ASC
                                                                            ? "transform rotate-180"
                                                                            : ""
                                                                    }
                                                                    src={
                                                                        isCurrentFieldMainSort
                                                                            ? sortDownIcon
                                                                            : sortIcon
                                                                    }
                                                                    alt="Sort"
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </PrimaryText>
                                                </div>
                                            )}
                                        </Draggable>
                                    )
                                })}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    )
}

interface IPoliciesListTableHeaderComponentProps {
    listType: PoliciesListType
    policies: IOneVerifyCoverageCheckHistory[]
    columns: IPolicyColumnConfiguration[]
    onChangeColumnOrder?: (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => void
    sortConfiguration?: ICoverageHistoryListSort
    onSetSortField?: (newSortField: AvailableCoverageHistorySortField) => void
    sortEnabled?: boolean
    policiesSelectable?: boolean
    selectedItems?: IOneVerifyCoverageCheckHistory[]
    onSelectAllItems?: () => void
    onDeselectAllItems?: () => void
    ignoreHorizontalPadding?: boolean
    isSinglePolicySelectable?: (item: IOneVerifyCoverageCheckHistory) => boolean
}
